import React, { ReactElement, useEffect } from 'react';

import { Spinner } from 'src/ui/components';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';

import { useTitle } from 'src/hooks';
import { useDownloadZip } from '../api/useDownladZip';

import { base64ToArrayBuffer } from 'src/utils/convertToBinaryFormat';
import { sanitizeFileName } from 'src/utils/common';

export default function DownloadZip(): ReactElement {
  useTitle('Download');
  const filePath = new URLSearchParams(window.location.search).get('filePath');

  const { data, isLoading, isError } = useDownloadZip(filePath);

  useEffect(() => {
    if (data?.Status !== 'Success') return;

    const fileName = sanitizeFileName(data?.FileName || 'download');

    const byteArray = base64ToArrayBuffer(data?.FileContent);

    const blob = new Blob([byteArray], {
      type: 'application/octet-stream',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [data]);

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 z-50 grid place-items-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 space-y-4 text-gray-600 dark:text-neutral-300">
            <Spinner size="large" />
            <div className="flex flex-col gap-1 text-center text-xl font-medium">
              <span>Preparing your download.</span>
              <span>Please keep this tab open.</span>
              <div className="flex flex-col gap-0 text-center text-base font-normal">
                <span>
                  You may need to allow pop-ups in order for the download to
                  begin.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isLoading && data?.Status === 'Success' && (
        <main className="grid flex-1 place-items-center overflow-y-auto overscroll-contain px-6 py-24 sm:py-32 lg:px-8">
          <div className="text-center">
            <p className="mt-5 text-base font-semibold leading-7 text-gray-600 dark:text-neutral-300">
              Your file has been downloaded. If you do not see it in your
              selected or default download location, try clicking the link in
              your email again.
            </p>
          </div>
        </main>
      )}

      {(isError ||
        filePath === undefined ||
        filePath === null ||
        data?.Status === 'File not found') && (
        <main className="grid flex-1 place-items-center overflow-y-auto overscroll-contain px-6 py-24 sm:py-32 lg:px-8">
          <div className="flex flex-col items-center  ">
            <ExclamationCircleIcon className="size-24 text-gray-500 dark:text-red-500" />
            <p className="mt-5 text-lg font-semibold leading-7 text-gray-600 dark:text-neutral-300">
              Something went wrong while preparing your download.
            </p>
            <p className="mt-2 text-base text-gray-600 dark:text-neutral-400">
              Please try clicking the link in your email again.
            </p>
          </div>
        </main>
      )}
    </>
  );
}
